import OpacityIcon from '@mui/icons-material/Opacity';
import {Box, Divider, Slider, Tooltip, Typography, styled} from '@mui/material';
import {Map, TileLayer, VectorLayer} from '@planet/layers';
import {useQuery} from '@tanstack/react-query';
import GeoJSON from 'ol/format/GeoJSON';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {getMosaics} from '../api.js';
import {MAPBOX_DARK} from '../config.js';
import {useClipCallbacks, useZoomCallback} from '../hooks/map.js';
import {mapBackground} from '../theme.js';
import {getExtentForGeometry, getVisualizationSeries} from '../util/map.js';
import {formatExtraParams, parseExtraParams} from '../util/params.js';
import {COLOR_SCALE, geometryStyle} from '../util/styles.js';
import {HomeLink} from './HomeLink.jsx';
import MapTools from './MapTools.jsx';
import MiniMap from './MiniMap.jsx';
import {MosaicsList} from './MosaicsList/index.jsx';
import RasterLayerDetailsDialog from './RasterLayerDetailsDialog.jsx';
import Sidebar from './Sidebar.js';

const defaultColor = COLOR_SCALE[COLOR_SCALE.length - 1].slice(1);

const classColors = {
  road: defaultColor,
  building: 'ffc',
  residential: 'f0e7c3',
  industrial: '7643b9',
  vegetation: '98c497',
  trees: '1c7119',
  water: '8ac0e6',
};

function getMosaicPairs(subscription, sources, targets) {
  const startTime = Date.parse(subscription.startTime);
  const endTime = subscription.endTime
    ? Date.parse(subscription.endTime)
    : null;

  const mosaicFilter = mosaic => {
    const first = Date.parse(mosaic.first_acquired);
    return startTime <= first && (endTime === null || first < endTime);
  };

  const sourceLookup = {};
  sources
    .filter(source => mosaicFilter(source))
    .forEach(source => {
      sourceLookup[source.first_acquired] = source;
    });

  const pairs = [];
  targets
    .filter(target => mosaicFilter(target))
    .forEach(target => {
      const source = sourceLookup[target.first_acquired];
      if (!source) {
        return;
      }

      pairs.push({
        firstAcquired: new Date(source.first_acquired),
        lastAcquired: new Date(source.last_acquired),
        source,
        target,
      });
    });

  return pairs.sort((a, b) => b.firstAcquired - a.firstAcquired);
}

const format = new GeoJSON();
function getClipGeometry(subscription) {
  return format.readGeometry(subscription.geometry, {
    featureProjection: 'EPSG:3857',
  });
}

function getBincat(feed) {
  let bincat = `0|${defaultColor}`;
  // TYOM feeds have an outputClasses field
  if (feed.outputClasses && feed.outputClasses.length > 0) {
    const mapping = [];
    feed.outputClasses.forEach((cls, i) => {
      // TYOM class colors start with #, e.g. '#ffffff'
      const color = cls.color.replace('#', '');
      mapping[i] = `${i}|${color}`;
    });
    bincat = mapping.join(',');
  }
  // some old multi-class feeds have class names in the feed process
  else if (feed.process && feed.process.actions) {
    for (const action of feed.process.actions) {
      const task = action['Fn::AddTask'];
      if (!task || task.length !== 2) {
        continue;
      }
      const args = task[1].args;
      if (args && args.output_class_names) {
        const potentialMapping = [];
        let allKnown = true;
        args.output_class_names.forEach((name, i) => {
          const color = classColors[name];
          potentialMapping[i] = `${i}|${color}`;
          if (!color) {
            allKnown = false;
          }
        });
        if (allKnown) {
          bincat = potentialMapping.join(',');
          break;
        }
      }
    }
  }
  return `&exp=bincat:${bincat}`;
}

const StyledOpacity = styled(OpacityIcon)(({theme}) => ({
  color: theme.palette.grey[500],
  marginRight: theme.spacing(1),
}));

const Container = styled('div')({
  overflow: 'auto',
  display: 'flex',
  flexGrow: 1,
});

const MainContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  position: 'relative',
});

const MapContainer = styled('div')({
  flexGrow: 1,
  position: 'relative',
  background: mapBackground,
});

const StyledSidebar = styled(Sidebar)({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  width: 350,
});

const StyledMiniMap = styled(MiniMap)(({theme}) => ({
  width: 96,
  height: 96,
  position: 'absolute',
  bottom: theme.spacing(3),
  left: theme.spacing(3),
}));

const clipStyle = new Style({
  stroke: new Stroke({
    width: 1,
    color: 'rgba(0,0,0,0)',
  }),
});

export const MosaicReport = props => {
  const history = useHistory();
  const params = useParams();
  const extraParams = useMemo(() => parseExtraParams(params), [params]);

  const clipGeometry = useMemo(
    () => getClipGeometry(props.subscription),
    [props.subscription],
  );

  const [view, updateView] = useState(
    extraParams.zoom && extraParams.center
      ? {
          zoom: extraParams.zoom,
          center: extraParams.center,
        }
      : {
          extent: getExtentForGeometry(props.subscription.geometry),
        },
  );

  const [selected, updateSelected] = useState(
    extraParams.selected ? extraParams.selected : 0,
  );
  const [opacity, updateOpacity] = useState(100);
  const [openLayerDetails, setOpenLayerDetails] = useState(null);

  const sourceSeriesId = getVisualizationSeries(props.feed);
  const targetSeriesId = props.feed.target.config.series_id;

  const sourceMosaics = useQuery({
    queryKey: ['mosaics', sourceSeriesId],
    queryFn: () => getMosaics(sourceSeriesId),
  });
  const targetMosaics = useQuery({
    queryKey: ['mosaics', targetSeriesId],
    queryFn: () => getMosaics(targetSeriesId),
  });

  const mosaicPairs = useMemo(() => {
    if (sourceMosaics.data && targetMosaics.data) {
      return getMosaicPairs(
        props.subscription,
        sourceMosaics.data,
        targetMosaics.data,
      );
    }
    return [];
  }, [props.subscription, sourceMosaics.data, targetMosaics.data]);

  useEffect(() => {
    if (view && view.center) {
      const extras = formatExtraParams({
        loc: view,
        selected,
      });
      history.replace(`/subscription/${props.subscription.id}/${extras}`);
    }
  }, [view, selected, history, props.subscription.id]);

  const onZoomClick = useZoomCallback(view, updateView);

  const onMapHomeClick = useCallback(() => {
    updateView({extent: getExtentForGeometry(props.subscription.geometry)});
  }, [props.subscription, updateView]);

  const [onPrecompose, onPostcompose] = useClipCallbacks(
    clipGeometry,
    clipStyle,
  );

  const onMiniMapClick = useCallback(
    event => {
      updateView({
        ...view,
        center: event.coordinate,
      });
    },
    [updateView, view],
  );

  const getTileUrl = useCallback(
    type => {
      const pair = mosaicPairs[selected];
      if (!pair) {
        return '';
      }

      let url = pair[type]._links.tiles;
      if (type === 'target') {
        url += getBincat(props.feed);
      }

      return url;
    },
    [mosaicPairs, props.feed, selected],
  );

  const onOpacityChange = useCallback(
    (event, value) => {
      updateOpacity(value);
    },
    [updateOpacity],
  );

  const handleOpenInBasemapsViewer = useCallback(
    mosaicName =>
      window.open(
        `https://insights.planet.com/data/mosaics?z=${view.zoom}&center=${view.center.toString()}&mosaic=${mosaicName}`,
        '_blank',
      ),
    [view],
  );

  // Open the Layer Details Dialog
  const handleSetOpenLayerDetails = useCallback(
    index => setOpenLayerDetails(mosaicPairs[index]),
    [mosaicPairs],
  );

  // Close the Layer Details Dialog
  const handleCloseDetailsDialog = () => setOpenLayerDetails(null);

  const geometry = props.subscription.geometry;

  return (
    <Container>
      <StyledSidebar>
        <Box py={1} px={2}>
          <HomeLink />
          <Box py={1}>
            <Tooltip title={props.subscription.title}>
              <Typography noWrap variant="h6">
                {props.subscription.title}
              </Typography>
            </Tooltip>
            <Typography noWrap variant="body1">
              {props.subscription.description}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <MosaicsList
          mosaicPairs={mosaicPairs}
          selected={selected}
          setSelected={idx => updateSelected(idx)}
          onDetailsClick={handleSetOpenLayerDetails}
        />
      </StyledSidebar>
      <MainContent>
        <MapContainer className="map-container">
          <Map
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
            }}
            animation={{duration: 300}}
            view={view}
            onViewChange={updateView}
          >
            <TileLayer url={MAPBOX_DARK} />
            <TileLayer
              url={getTileUrl('source')}
              onPreRender={onPrecompose}
              onPostRender={onPostcompose}
            />
            <TileLayer
              url={getTileUrl('target')}
              opacity={opacity / 100}
              onPreRender={onPrecompose}
              onPostRender={onPostcompose}
            />
            <VectorLayer data={geometry} layerStyle={geometryStyle} />
          </Map>
          <StyledMiniMap
            extent={view.extent}
            geometry={geometry}
            onClick={onMiniMapClick}
          />
          <MapTools onHomeClick={onMapHomeClick} onZoomClick={onZoomClick} />
        </MapContainer>
        <Tooltip title="Overlay Opacity">
          <Box
            position="absolute"
            zIndex={9}
            bgcolor="white"
            width={200}
            padding={1}
            display="flex"
            pr={2}
            sx={{
              top: theme => theme.spacing(2),
              right: theme => theme.spacing(2),
            }}
          >
            <StyledOpacity />
            <Box flexGrow={1}>
              <Slider value={opacity} onChange={onOpacityChange} size="small" />
            </Box>
          </Box>
        </Tooltip>
        <RasterLayerDetailsDialog
          bincat={getBincat(props.feed)}
          layer={openLayerDetails}
          onClose={handleCloseDetailsDialog}
          onBasemapViewerClick={handleOpenInBasemapsViewer}
        />
      </MainContent>
    </Container>
  );
};

MosaicReport.propTypes = {
  subscription: PropTypes.object,
  feed: PropTypes.object,
};

export default MosaicReport;
